import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Home, LogOut, Menu, X, Map, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Wrapper component that uses hooks
function AdminLayoutWrapper(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  // Try to read the public user email if logged in (unified-access)
  const userEmailFromUserCtx = (() => {
    try {
      const tok = sessionStorage.getItem('user_token');
      if (!tok) return null;
      // Decode without verification (just for display)
      const payload = JSON.parse(atob(tok.split('.')[1]));
      return payload.email || null;
    } catch (e) {
      return null;
    }
  })();
  
  return (
    <AdminLayoutInner 
      location={location} 
      navigate={navigate} 
      auth={auth}
      bare={props.bare}
      userEmail={userEmailFromUserCtx}
    >
      {props.children}
    </AdminLayoutInner>
  );
}

// Class component to avoid babel plugin issues
class AdminLayoutInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { sidebarOpen: false };
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.closeSidebar = this.closeSidebar.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
  }

  toggleSidebar() {
    this.setState({ sidebarOpen: !this.state.sidebarOpen });
  }

  closeSidebar() {
    this.setState({ sidebarOpen: false });
  }

  handleLogout() {
    // Clear both admin and user tokens (unified-access logout).
    // sessionStorage can throw in private browsing modes; silently continue on failure.
    try {
      sessionStorage.removeItem('user_token');
      sessionStorage.removeItem('admin_token');
      // Also clear any legacy localStorage tokens from older versions
      localStorage.removeItem('user_token');
      localStorage.removeItem('admin_token');
    } catch (err) {
      console.warn('Could not clear tokens from storage:', err);
    }
    this.props.auth.logout();
    // Full reload to reset both auth contexts
    window.location.replace('/login');
  }

  isActive(path) {
    return this.props.location.pathname === path;
  }

  render() {
    const { sidebarOpen } = this.state;
    // Prefer the public user email if both contexts are logged in (unified-access user)
    const userEmail = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('user_token'))
      ? (this.props.userEmail || (this.props.auth.user && this.props.auth.user.email) || '')
      : (this.props.auth.user ? this.props.auth.user.email : '');

    return (
      <div className="min-h-screen bg-black" data-testid="admin-layout">
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10 h-16 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/branding/bakrajo_logo.png" alt="Bakrajo Hills" className="h-8 w-auto" />
            <span className="text-white text-sm tracking-[0.15em] font-medium uppercase">Bakrajo Hills</span>
          </div>
          <button onClick={this.toggleSidebar} className="p-2 text-white/60 hover:text-white" data-testid="mobile-sidebar-toggle">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <aside className={'fixed top-0 left-0 h-full w-64 bg-[#0a0a0a] border-r border-white/10 z-40 transition-transform duration-300 lg:translate-x-0 ' + (sidebarOpen ? 'translate-x-0' : '-translate-x-full')} data-testid="admin-sidebar">
          <div className="flex flex-col h-full">
            <div className="h-20 flex items-center px-6 border-b border-white/10 gap-3">
              <img src="/branding/bakrajo_logo.png" alt="Bakrajo Hills" className="h-10 w-auto flex-shrink-0" />
              <span className="text-white text-sm tracking-[0.15em] font-medium uppercase">Bakrajo Hills</span>
            </div>

            <nav className="flex-1 py-8 px-4 space-y-1">
              <Link to="/admin" onClick={this.closeSidebar} className={'flex items-center gap-3 px-4 py-3 transition-all duration-200 ' + (this.isActive('/admin') ? 'bg-white text-black' : 'text-white/60 hover:text-white hover:bg-white/5')} data-testid="admin-nav-dashboard">
                <LayoutDashboard className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Dashboard</span>
              </Link>
              <Link to="/admin/projects" onClick={this.closeSidebar} className={'flex items-center gap-3 px-4 py-3 transition-all duration-200 ' + (this.isActive('/admin/projects') ? 'bg-white text-black' : 'text-white/60 hover:text-white hover:bg-white/5')} data-testid="admin-nav-projects">
                <Building2 className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Projects</span>
              </Link>
              <Link to="/admin/units" onClick={this.closeSidebar} className={'flex items-center gap-3 px-4 py-3 transition-all duration-200 ' + (this.isActive('/admin/units') ? 'bg-white text-black' : 'text-white/60 hover:text-white hover:bg-white/5')} data-testid="admin-nav-units">
                <Home className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Units</span>
              </Link>
              <Link to="/admin/map-editor" onClick={this.closeSidebar} className={'flex items-center gap-3 px-4 py-3 transition-all duration-200 ' + (this.isActive('/admin/map-editor') ? 'bg-white text-black' : 'text-white/60 hover:text-white hover:bg-white/5')} data-testid="admin-nav-map-editor">
                <Map className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Map Editor</span>
              </Link>
              <Link to="/admin/sync" onClick={this.closeSidebar} className={'flex items-center gap-3 px-4 py-3 transition-all duration-200 ' + (this.isActive('/admin/sync') ? 'bg-white text-black' : 'text-white/60 hover:text-white hover:bg-white/5')} data-testid="admin-nav-sync">
                <RefreshCw className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Sheets Sync</span>
              </Link>
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="mb-4 px-4">
                <p className="text-[10px] uppercase tracking-[0.15em] text-white/30 mb-1 font-light">Logged in as</p>
                <p className="text-xs text-white/70 font-light truncate">{userEmail}</p>
              </div>
              <button onClick={this.handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-white/40 hover:text-white hover:bg-white/5 transition-colors" data-testid="admin-logout-btn">
                <LogOut className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">Logout</span>
              </button>
              <Link to="/" className="w-full flex items-center gap-3 px-4 py-3 text-white/40 hover:text-white hover:bg-white/5 transition-colors mt-1" data-testid="admin-view-site-btn">
                <ExternalLink className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.15em] font-light">View Website</span>
              </Link>
            </div>
          </div>
        </aside>

        {sidebarOpen && <div className="fixed inset-0 bg-black/80 z-30 lg:hidden" onClick={this.closeSidebar}></div>}

        <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
          {/* Top header bar with prominent "Back to Main Page" button (hidden in bare mode where the main page IS the content) */}
          {!this.props.bare && (
            <div className="hidden lg:flex sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-white/10 h-14 items-center justify-end px-10">
              <Link
                to="/"
                data-testid="admin-back-to-main-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#C5A059] transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Back to Main Page
              </Link>
            </div>
          )}
          {/* Mobile back-to-main button */}
          {!this.props.bare && (
            <div className="lg:hidden px-4 pt-3">
              <Link
                to="/"
                data-testid="admin-back-to-main-btn-mobile"
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#C5A059] transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Back to Main Page
              </Link>
            </div>
          )}
          <div className={this.props.bare ? '' : 'p-6 lg:p-10'}>{this.props.children}</div>
        </main>
      </div>
    );
  }
}

export const AdminLayout = AdminLayoutWrapper;
export default AdminLayoutWrapper;
